import type { IAPIRequestConfig, IHTTPResponse } from '@yimoka/shared';
import { createWebHttpClient, type IWebHttpClient } from '@yimoka/web-http';

import { getEnv } from '@/env';
import { getCurrentLanguage } from '@/locales/language';
import { getClientID, getRealm, getStaffToken, getTenantID, getUserToken } from '@/session/auth-session';

import { installTenantAuthRecovery } from './tenant-auth-recovery';

export type TenantHTTPRecoveryMode = 'skip';
export type TenantHTTPRequestConfig<P> = Omit<IAPIRequestConfig<P>, 'url' | 'method'> & {
  /** tenantAuthRecovery 是 tenant-web 内部控制项，只影响本地 401 拦截器，不会作为请求头发给后端。 */
  tenantAuthRecovery?: TenantHTTPRecoveryMode;
};

let platformHttpClient: IWebHttpClient | undefined;
let tenantHttpClient: IWebHttpClient | undefined;

/** getPlatformHttpClient 延迟创建使用站点 Realm 的平台用户身份请求客户端。 */
function getPlatformHttpClient() {
  if (!platformHttpClient) {
    const env = getEnv();

    platformHttpClient = createWebHttpClient({
      baseURL: env.apiBaseURL,
      useType: 'platform',
      platform: typeof navigator === 'undefined' ? 'web' : navigator.platform,
      app: env.appId,
      getAuthorization: getUserToken,
      getTenantID: () => '1000',
      // 登录前还没有租户会话，平台身份必须使用部署站点 Realm 才能进入对应渠道。
      getRealm: () => env.realm,
      getLanguage: getCurrentLanguage,
      getClientID,
    });
  }

  return platformHttpClient;
}

/** getTenantHttpClient 创建优先使用已选租户 Realm 的租户客户端，并安装 Host/Remote 共用的 401 恢复协议。 */
function getTenantHttpClient() {
  if (!tenantHttpClient) {
    const env = getEnv();

    const client = createWebHttpClient({
      baseURL: env.apiBaseURL,
      useType: 'tenant',
      platform: typeof navigator === 'undefined' ? 'web' : navigator.platform,
      app: env.appId,
      getAuthorization: () => getStaffToken() || getUserToken(),
      getTenantID: () => getTenantID() || env.tenantID,
      getRealm: () => getRealm() || env.realm,
      getLanguage: getCurrentLanguage,
      getClientID,
    });
    installTenantAuthRecovery({
      getPlatformExecutor: () => getPlatformHttpClient().apiExecutor,
      tenantClient: client.instance,
    });
    tenantHttpClient = client;
  }

  return tenantHttpClient;
}

const httpTarget: IWebHttpClient['instance'] = Object.create(null);
const platformHttpTarget: IWebHttpClient['instance'] = Object.create(null);
const tenantHttpTarget: IWebHttpClient['instance'] = Object.create(null);

export const http = new Proxy(httpTarget, {
  get: (_target, property, receiver) => Reflect.get(getTenantHttpClient().instance, property, receiver),
});

export const platformHttp = new Proxy(platformHttpTarget, {
  get: (_target, property, receiver) => Reflect.get(getPlatformHttpClient().instance, property, receiver),
});

export const tenantHttp = new Proxy(tenantHttpTarget, {
  get: (_target, property, receiver) => Reflect.get(getTenantHttpClient().instance, property, receiver),
});

export const platformApiExecutor: IWebHttpClient['apiExecutor'] = (...args) => getPlatformHttpClient().apiExecutor(...args);
export const tenantApiExecutor: IWebHttpClient['apiExecutor'] = (...args) => getTenantHttpClient().apiExecutor(...args);

export const apiExecutor = tenantApiExecutor;

/** request 把项目级请求配置交给指定执行器并保留响应泛型。 */
function request<R, P>(
  executor: IWebHttpClient['apiExecutor'],
  config: IAPIRequestConfig<P>,
): Promise<IHTTPResponse<R, P>> {
  return executor(config) as Promise<IHTTPResponse<R, P>>;
}

/** platformHttpGet 发送平台用户态 GET 请求。 */
export function platformHttpGet<R, P = unknown>(url: string, config?: Omit<IAPIRequestConfig<P>, 'url' | 'method'>) {
  return request<R, P>(platformApiExecutor, { ...config, url, method: 'GET' });
}

/** platformHttpPost 发送平台用户态 POST 请求。 */
export function platformHttpPost<R, P = unknown>(
  url: string,
  data?: P,
  config?: Omit<IAPIRequestConfig<P>, 'url' | 'method' | 'data'>,
) {
  return request<R, P>(platformApiExecutor, { ...config, url, method: 'POST', data });
}

/** tenantHttpGet 发送租户员工态 GET 请求，并支持端内恢复控制项。 */
export function tenantHttpGet<R, P = unknown>(url: string, config?: TenantHTTPRequestConfig<P>) {
  return request<R, P>(tenantApiExecutor, { ...config, url, method: 'GET' });
}

/** tenantHttpPost 发送租户员工态 POST 请求，并支持端内恢复控制项。 */
export function tenantHttpPost<R, P = unknown>(
  url: string,
  data?: P,
  config?: Omit<TenantHTTPRequestConfig<P>, 'data'>,
) {
  return request<R, P>(tenantApiExecutor, { ...config, url, method: 'POST', data });
}

export const httpGet = tenantHttpGet;
export const httpPost = tenantHttpPost;
